/**
 * Single source of truth for the site's URL map.
 *
 * Every path here mirrors a URL that exists on the live Webflow site. Phase 8
 * builds the redirect map from this list, so paths must not drift from
 * production without a matching redirect.
 */

export type PageTemplate = 'marketing' | 'post' | 'support' | 'legal'

/**
 * Routes that have a dedicated component rather than a generated page spec.
 * Everything else in ALL_ROUTES renders through `MarketingPage`.
 */
export const CUSTOM_ROUTES: Record<string, 'blogIndex' | 'supportIndex' | 'outboundStub'> = {
  '/blog': 'blogIndex',
  '/support': 'supportIndex',
  '/outbound': 'outboundStub',
}

export interface RouteDef {
  /** URL path, no trailing slash (except root). */
  path: string
  /** Which template renders it. */
  template: PageTemplate
  /** Directory the page was captured from, in the retired mirror. Provenance only. */
  mirrorDir: string
  /** Set when the mirror does not contain usable source for this route. */
  sourceStatus?: 'missing' | 'recovered'
  /** Human note explaining a non-clean source. */
  sourceNote?: string
}

export const MARKETING_ROUTES: RouteDef[] = [
  {
    path: '/',
    template: 'marketing',
    mirrorDir: '.',
    sourceStatus: 'recovered',
    sourceNote: 'Wayback 2026-02-01 snapshot; see docs/HOMEPAGE-RECOVERY.md',
  },
  { path: '/about', template: 'marketing', mirrorDir: 'about' },
  { path: '/agencies', template: 'marketing', mirrorDir: 'agencies' },
  { path: '/ai-email-editor', template: 'marketing', mirrorDir: 'ai-email-editor' },
  { path: '/blog', template: 'marketing', mirrorDir: 'blog' },
  { path: '/demo', template: 'marketing', mirrorDir: 'demo' },
  { path: '/events', template: 'marketing', mirrorDir: 'events' },
  { path: '/hubspot-integration', template: 'marketing', mirrorDir: 'hubspot-integration' },
  { path: '/hubspot-nurture-agent-vs-singulate', template: 'marketing', mirrorDir: '' },
  { path: '/lifecycle-marketing', template: 'marketing', mirrorDir: 'lifecycle-marketing' },
  { path: '/marketing-automation', template: 'marketing', mirrorDir: 'marketing-automation' },
  {
    path: '/modular-communication-strategy-architecture-whitepaper',
    template: 'marketing',
    mirrorDir: 'modular-communication-strategy-architecture-whitepaper',
  },
  { path: '/newsletter', template: 'marketing', mirrorDir: 'newsletter' },
  {
    path: '/outbound',
    template: 'marketing',
    mirrorDir: 'outbound',
    sourceStatus: 'missing',
    sourceNote: 'Mirror captured Webflow 404; needs browser re-capture (plan §8)',
  },
  { path: '/roi-calculator', template: 'marketing', mirrorDir: 'roi-calculator' },
  { path: '/sales', template: 'marketing', mirrorDir: 'sales' },
  {
    path: '/singulate-certified-partner-program',
    template: 'marketing',
    mirrorDir: 'singulate-certified-partner-program',
  },
  { path: '/startups', template: 'marketing', mirrorDir: 'startups' },
  { path: '/support', template: 'marketing', mirrorDir: 'support' },

  /*
   * Recovered from the live site on 2026-09-04. The Webflow capture never
   * held these, so they have no mirrorDir; they are authored against the
   * design system and render through `MarketingPage`, not `PortedPage`.
   */
  { path: '/data-cleanup', template: 'marketing', mirrorDir: '' },
  { path: '/segmentation', template: 'marketing', mirrorDir: '' },
  { path: '/generative-content', template: 'marketing', mirrorDir: '' },
  { path: '/email-editor', template: 'marketing', mirrorDir: '' },
  { path: '/try-singulate-for-free', template: 'marketing', mirrorDir: '' },

  /* Studio launch, 2026-10. Authored against the design system. */
  { path: '/studio', template: 'marketing', mirrorDir: '' },
  { path: '/pricing', template: 'marketing', mirrorDir: '' },

  /* Studio vertical for independent jewelers, 2026-10. Authored against the design system. */
  { path: '/studio-for-jewelers', template: 'marketing', mirrorDir: '' },

  /* Weekly live session, 2026-10. Authored against the design system. */
  { path: '/sendhelp', template: 'marketing', mirrorDir: '' },
]

export const LEGAL_ROUTES: RouteDef[] = [
  { path: '/privacy', template: 'legal', mirrorDir: 'privacy' },
  { path: '/terms', template: 'legal', mirrorDir: 'terms' },
]

export const ALL_ROUTES: RouteDef[] = [...MARKETING_ROUTES, ...LEGAL_ROUTES]
